/**
 * Sincronização automática de calendários iCal (Booking/Airbnb) para hospedagens.
 * Roda a cada 30 min quando o admin está aberto. Atualiza localStorage.
 * Primeira execução após ~45s (idle) para não competir com carregamento inicial / sync AWS.
 * Custo zero: sem Lambda, sem cron; usa a sessão do usuário no navegador.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getHospedagens, saveHospedagem } from '../lib/hospedagemStorage';
import { fetchBlockedDatesFromUrls } from '../lib/icalSync';

const INTERVAL_MS = 30 * 60 * 1000; // 30 minutos
/** Não competir com o primeiro paint / hidratação do admin nem com sync AWS logo após login. */
const INITIAL_DELAY_MS = 45 * 1000;

const SYNC_EVENT = 'naviex-hospedagem-ical-synced';

/** Dispara evento para que HospedagensPage (e outros) atualizem a UI. */
export function dispatchIcalSynced() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SYNC_EVENT));
  }
}

export function useOnIcalSynced(callback: () => void) {
  useEffect(() => {
    const handler = () => callback();
    window.addEventListener(SYNC_EVENT, handler);
    return () => window.removeEventListener(SYNC_EVENT, handler);
  }, [callback]);
}

async function runIcalSync() {
  const hospedagens = getHospedagens();
  let updated = 0;
  for (const h of hospedagens) {
    const urls = (h.icalConfig?.icalUrls ?? [])
      .map((u) => u.url?.trim())
      .filter((u): u is string => !!u && u.startsWith('http'));
    if (urls.length === 0) continue;

    try {
      const blocked = await fetchBlockedDatesFromUrls(urls);
      const nextIcal = {
        ...h.icalConfig,
        blockedDatesFromIcal: blocked,
        lastSyncAt: new Date().toISOString(),
      };
      saveHospedagem({
        ...h,
        icalConfig: nextIcal,
      });
      updated++;
    } catch (e) {
      console.warn('[IcalAutoSync] Erro em', h.nome, e);
    }
  }
  if (updated > 0) {
    dispatchIcalSynced();
  }
}

export default function IcalAutoSyncProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!user) return;

    const schedule = () => {
      void runIcalSync();
    };

    // Primeira execução após delay
    const t0 = setTimeout(schedule, INITIAL_DELAY_MS);

    // Depois a cada 30 min
    intervalRef.current = setInterval(schedule, INTERVAL_MS);

    return () => {
      clearTimeout(t0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user]);

  return <>{children}</>;
}
